import { useEffect, useRef, useState } from "react";
import { trackClientEvent } from "./analytics";
import { CityPanel } from "./components/CityPanel";
import { AdminPage } from "./components/AdminPage";
import { PurchaseStatusPage } from "./components/PurchaseStatusPage";
import { TurkeyCityMap } from "./components/TurkeyCityMap";
import { useCityMap } from "./hooks/use-city-map";

export function App() {
  if (window.location.pathname === "/admin") return <AdminPage />;
  const purchaseMatch = window.location.pathname.match(/^\/purchase\/([^/]+)$/u);
  if (purchaseMatch) {
    const capabilityToken = new URLSearchParams(window.location.search).get("capability") ?? "";
    return (
      <PurchaseStatusPage
        attemptId={decodeURIComponent(purchaseMatch[1])}
        capabilityToken={capabilityToken}
      />
    );
  }

  return <MapApplication />;
}

export function PublicHero() {
  return (
    <section className="hero" aria-labelledby="page-title">
      <div>
        <p className="eyebrow">Türkiye'nin günlük şehir haritası</p>
        <h1 id="page-title">
          Bugün şehir
          <br />
          ne söylüyor?
        </h1>
      </div>
      <p className="hero__intro">
        81 şehir. Her gün, her şehir için tek görünür mesaj. Sözü devralan kişi bugünün sesini
        belirler.
      </p>
    </section>
  );
}

function MapApplication() {
  const [selectedCode, setSelectedCode] = useState<string | null>(() => cityCodeFromLocation());
  const { map, city, refreshMap, refreshCity } = useCityMap(selectedCode);
  const trackedMapDate = useRef<string | null>(null);

  useEffect(() => {
    const syncSelectedCityFromUrl = () => setSelectedCode(cityCodeFromLocation());
    window.addEventListener("popstate", syncSelectedCityFromUrl);
    return () => window.removeEventListener("popstate", syncSelectedCityFromUrl);
  }, []);

  useEffect(() => {
    const localDate = map.data?.localDate;
    if (!localDate || trackedMapDate.current === localDate) return;
    trackedMapDate.current = localDate;
    const source =
      new URLSearchParams(window.location.search).get("ref") === "share" ? "shared_link" : "direct";
    trackClientEvent("map_view", { source });
    if (source === "shared_link") trackClientEvent("share_referral", { source });
    const previousVisit = window.localStorage.getItem("sehrin-sesi.last-visit-date.v1");
    if (previousVisit && previousVisit !== localDate) {
      trackClientEvent("return_visit", { source: "direct" });
    }
    window.localStorage.setItem("sehrin-sesi.last-visit-date.v1", localDate);
  }, [map.data?.localDate]);

  function selectCity(code: string) {
    if (code === selectedCode) return;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("city", code);
    window.history.pushState(null, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setSelectedCode(code);
    trackClientEvent("city_detail", { cityCode: code, source: "internal" });
  }

  return (
    <>
      <header className="site-header">
        <a className="wordmark" href="/" aria-label="Şehrin Sesi ana sayfa">
          Şehrin Sesi
        </a>
        <span className="date-chip">{map.data?.localDate ?? "Bugün"} · İstanbul</span>
      </header>
      <main className="app-shell">
        <PublicHero />

        {map.data?.availability.readOnly && (
          <div className="availability-banner" role="status">
            <strong>Harita açık, yayın kapalı.</strong> Son doğrulanmış şehir durumlarını
            görüntüleyebilirsin; şu anda yeni ödeme alınmıyor.
          </div>
        )}

        <section className="workspace" aria-label="Şehir haritası ve detayları">
          <div className="map-column">
            <div className="map-toolbar">
              <p>
                {map.loading
                  ? "Harita yükleniyor…"
                  : map.error && !map.data
                    ? "Harita şu anda kullanılamıyor."
                    : `${map.data?.cities.length ?? 0} şehir güncel`}
              </p>
              <button type="button" onClick={() => void refreshMap()} disabled={map.loading}>
                Yenile <span aria-hidden="true">↻</span>
              </button>
            </div>
            {map.data ? (
              <TurkeyCityMap
                cities={map.data.cities}
                selectedCode={selectedCode}
                onSelect={selectCity}
              />
            ) : (
              <div className="map-placeholder" role="status">
                <span />
                <p>{map.error ?? "81 şehir hazırlanıyor…"}</p>
              </div>
            )}
            {map.stale && (
              <p className="map-stale">
                Bağlantı yenilenemedi; son doğrulanmış harita gösteriliyor.
              </p>
            )}
          </div>
          <CityPanel
            key={city.data?.code ?? "no-city"}
            city={city.data}
            loading={city.loading}
            stale={city.stale}
            error={city.error}
            onRefresh={() => (selectedCode ? refreshCity(selectedCode) : false)}
          />
        </section>
      </main>
      <footer className="site-footer">
        <span>Şehrin Sesi · 2026</span>
        <span>Her gece 00:00'da yeni bir gün başlar.</span>
      </footer>
    </>
  );
}

function cityCodeFromLocation() {
  const cityCode = new URLSearchParams(window.location.search).get("city");
  return cityCode && /^TR-\d{2}$/u.test(cityCode) ? cityCode : null;
}
